/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { requestMaterialClassificationAI } from '../lib/aiServiceClient';

const RegisterItemPage = ({
  isEditing = false,
  handleRegisterSubmit = (e) => e.preventDefault(),
  regType = 'วัสดุสิ้นเปลือง',
  setRegType = () => {},
  regName = '',
  handleNameChange = () => {},
  isNameDuplicate = false,
  nameSuggestions = [],
  regAliases = '',
  setRegAliases = () => {},
  durableType = '',
  setDurableType = () => {},

  // Location inputs
  cabinet = '',
  setCabinet = () => {},
  shelf = '',
  setShelf = () => {},
  row = '',
  setRow = () => {},

  // Units
  regUnit,
  setRegUnit,
  unit = '',
  setUnit = () => {},
  regUnitSize,
  setRegUnitSize,
  unitSize = '',
  setUnitSize = () => {},

  // Lot, Stock, & Expiry
  lotNumber = '',
  setLotNumber = () => {},
  expDate = '',
  setExpDate = () => {},
  initAmount,
  setInitAmount,
  initialQty = '',
  setInitialQty = () => {},
  minStock = '',
  setMinStock = () => {},
  globalMinStock = '',
  setGlobalMinStock = () => {},

  // Purchase info
  pricePerUnit = '',
  setPricePerUnit = () => {},
  barcode = '',
  setBarcode = () => {},
  company = '',
  setCompany = () => {},
  receiveDate = '',
  setReceiveDate = () => {},
  deliveryNote = '',
  setDeliveryNote = () => {},
  poNumber = '',
  setPoNumber = () => {},
  orderDate = '',
  setOrderDate = () => {},
  approvalDate = '',
  setApprovalDate = () => {},

  // 4-Slot Image Upload
  regImages,
  handleSlotImageChange,
  handleSlotImageRemove,
  isUploadingSlot,
  imageUrl = '',
  setImageUrl = () => {},
  zoomImageUrl = '',
  setZoomImageUrl = () => {},
  otherImageUrl = '',
  setOtherImageUrl = () => {},
  documentImageUrl = '',
  setDocumentImageUrl = () => {},
  isUploading = false,
  isUploadingZoom = false,
  isUploadingOther = false,
  isUploadingDocument = false,
  handleImageUpload,

  // Actions & context
  setExpandedImage = () => {},
  resetForm = () => {},
  editId = null,
  itemMaster = [],
  supplies = []
}) => {
  // Safe fallbacks for unit and unit size
  const currentUnit = regUnit !== undefined ? regUnit : unit;
  const handleUnitUpdate = (val) => {
    if (setRegUnit) setRegUnit(val);
    if (setUnit) setUnit(val);
  };

  const currentUnitSize = regUnitSize !== undefined ? regUnitSize : unitSize;
  const handleUnitSizeUpdate = (val) => {
    if (setRegUnitSize) setRegUnitSize(val);
    if (setUnitSize) setUnitSize(val);
  };

  // Safe fallback for initial amount
  const currentInitAmount = initAmount !== undefined ? initAmount : initialQty;
  const handleInitAmountUpdate = (val) => {
    if (setInitAmount) setInitAmount(val);
    if (setInitialQty) setInitialQty(val);
  };

  // Safe image values
  const fullImg = (regImages && (regImages.full || (Array.isArray(regImages) ? regImages[0] : ''))) || imageUrl || '';
  const zoomImg = (regImages && (regImages.zoom || (Array.isArray(regImages) ? regImages[1] : ''))) || zoomImageUrl || '';
  const otherImg = (regImages && (regImages.other || (Array.isArray(regImages) ? regImages[2] : ''))) || otherImageUrl || '';
  const docImg = (regImages && (regImages.document || (Array.isArray(regImages) ? regImages[3] : ''))) || documentImageUrl || '';

  // Safe upload states
  const isUpFull = (typeof isUploadingSlot === 'function' ? isUploadingSlot('full') : (isUploadingSlot?.full || isUploadingSlot === 'full')) || isUploading;
  const isUpZoom = (typeof isUploadingSlot === 'function' ? isUploadingSlot('zoom') : (isUploadingSlot?.zoom || isUploadingSlot === 'zoom')) || isUploadingZoom;
  const isUpOther = (typeof isUploadingSlot === 'function' ? isUploadingSlot('other') : (isUploadingSlot?.other || isUploadingSlot === 'other')) || isUploadingOther;
  const isUpDoc = (typeof isUploadingSlot === 'function' ? isUploadingSlot('document') : (isUploadingSlot?.document || isUploadingSlot === 'document')) || isUploadingDocument;
  const isAnyUploading = isUpFull || isUpZoom || isUpOther || isUpDoc;

  // Safe upload & remove callbacks
  const onFileChange = (e, slot) => {
    if (handleSlotImageChange) {
      handleSlotImageChange(e, slot);
    } else if (handleImageUpload) {
      handleImageUpload(e, slot);
    }
  };

  const onFileRemove = (slot) => {
    if (handleSlotImageRemove) {
      handleSlotImageRemove(slot);
    } else {
      if (slot === 'full' && setImageUrl) setImageUrl('');
      if (slot === 'zoom' && setZoomImageUrl) setZoomImageUrl('');
      if (slot === 'other' && setOtherImageUrl) setOtherImageUrl('');
      if (slot === 'document' && setDocumentImageUrl) setDocumentImageUrl('');
    }
  };

  // Safe name suggestions
  const displaySuggestions = (nameSuggestions && nameSuggestions.length > 0)
    ? nameSuggestions
    : (regName && regName.trim().length > 1 && itemMaster && itemMaster.length > 0)
      ? itemMaster.filter(m => {
          const nMatch = m.item_name && m.item_name.toLowerCase().includes(regName.toLowerCase());
          const aMatch = m.aliases && m.aliases.toLowerCase().includes(regName.toLowerCase());
          return nMatch || aMatch;
        }).slice(0, 5)
      : [];

  // AI Visual Assist state
  const [isAnalyzingVisual, setIsAnalyzingVisual] = useState(false);
  const [visualAiResult, setVisualAiResult] = useState(null);
  const [visualAiError, setVisualAiError] = useState(null);

  const applyVisualAiResult = (data) => {
    const targetData = data || visualAiResult;
    if (!targetData) return;

    const suggestedName = targetData.thaiName || targetData.className || '';
    if (suggestedName && handleNameChange) {
      handleNameChange(suggestedName);
    }

    const cat = targetData.suggestedCategory || '';
    if (setRegType) {
      if (cat.includes('หัวกรอ') || cat.toLowerCase().includes('bur')) {
        setRegType('หัวกรอ');
      } else if (cat.includes('คงทน') || cat.toLowerCase().includes('durable')) {
        setRegType('วัสดุคงทน');
      } else if (cat.includes('สิ้นเปลือง') || cat.toLowerCase().includes('consumable')) {
        setRegType('วัสดุสิ้นเปลือง');
      } else if (cat) {
        setRegType(cat);
      }
    }
  };

  const handleAiVisualAssist = async () => {
    const targetImage = fullImg || zoomImg || otherImg || docImg;
    if (!targetImage) {
      alert('กรุณาเลือกหรืออัปโหลดรูปภาพวัสดุก่อนใช้ระบบ AI วิเคราะห์ภาพ');
      return;
    }

    setIsAnalyzingVisual(true);
    setVisualAiError(null);

    try {
      const result = await requestMaterialClassificationAI(targetImage);
      setVisualAiResult(result);

      const suggestedName = result.thaiName || result.className || '';
      let cat = result.suggestedCategory || '';
      if (cat.includes('หัวกรอ') || cat.toLowerCase().includes('bur')) cat = 'หัวกรอ';
      else if (cat.includes('คงทน') || cat.toLowerCase().includes('durable')) cat = 'วัสดุคงทน';
      else if (cat.includes('สิ้นเปลือง') || cat.toLowerCase().includes('consumable')) cat = 'วัสดุสิ้นเปลือง';

      const conf = result.confidence
        ? ` (ความมั่นใจ ${Math.round(result.confidence > 1 ? result.confidence : result.confidence * 100)}%)`
        : '';

      const confirmMsg = `🤖 AI วิเคราะห์ภาพสำเร็จ!\n\nชื่อที่ตรวจพบ: ${suggestedName || '-'}\nหมวดหมู่แนะนำ: ${cat || '-'}${conf}\n\nต้องการนำข้อมูลนี้ไปกรอกในฟอร์มอัตโนมัติหรือไม่?`;

      if (window.confirm(confirmMsg)) {
        applyVisualAiResult(result);
      }
    } catch (err) {
      console.error('Visual AI classification failed:', err);
      const errMsg = err?.message || 'ไม่สามารถเชื่อมต่อกับ AI Server ได้';
      setVisualAiError(errMsg);
      alert(`การวิเคราะห์ภาพล้มเหลว: ${errMsg}`);
    } finally {
      setIsAnalyzingVisual(false);
    }
  };

  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      padding: '30px',
      borderRadius: '24px',
      boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)',
      color: '#ffffff',
      maxWidth: '680px',
      margin: '0 auto',
      fontFamily: "'Segoe UI', 'Noto Sans Thai', sans-serif"
    }}>
      <h2 style={{
        textAlign: 'center',
        color: '#ffffff',
        marginBottom: '30px',
        fontWeight: '700',
        textShadow: '0 2px 10px rgba(0,0,0,0.3)',
        fontSize: '1.8em'
      }}>
        {isEditing ? '✏️ แก้ไขข้อมูลวัสดุ' : '📋 ลงทะเบียนวัสดุใหม่'}
      </h2>

      <form onSubmit={handleRegisterSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {!isEditing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>1. ประเภทวัสดุ *</label>
            <select
              value={regType}
              onChange={(e) => setRegType(e.target.value)}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#ffffff',
                fontSize: '1em',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="วัสดุสิ้นเปลือง" style={{ background: '#1a0c30', color: '#fff' }}>📦 วัสดุสิ้นเปลือง</option>
              <option value="วัสดุคงทน" style={{ background: '#1a0c30', color: '#fff' }}>🏢 วัสดุคงทน</option>
              <option value="หัวกรอ" style={{ background: '#1a0c30', color: '#fff' }}>🦷 หัวกรอ</option>
            </select>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>{isEditing ? '2.' : '2.'} ชื่อวัสดุ *</label>
          <input
            value={regName}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="พิมพ์ชื่อวัสดุ..."
            style={{
              padding: '12px',
              borderRadius: '8px',
              border: isNameDuplicate && !isEditing ? '2px solid #e67e22' : '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#ffffff',
              fontSize: '1em',
              outline: 'none',
              transition: 'all 0.3s'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#1abc9c';
              e.target.style.background = 'rgba(255, 255, 255, 0.12)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = isNameDuplicate && !isEditing ? '#e67e22' : 'rgba(255, 255, 255, 0.15)';
              e.target.style.background = 'rgba(255, 255, 255, 0.08)';
            }}
            required
          />
          {!isEditing && isNameDuplicate && <small style={{ color: '#e67e22', fontWeight: 'bold' }}>⚠️ ชื่อนี้มีในระบบแล้ว (กดยืนยันเพื่อเพิ่ม Lot ใหม่)</small>}

          {/* Smart Suggestion */}
          {!isEditing && displaySuggestions.length > 0 && (
            <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <small style={{ color: 'rgba(255, 255, 255, 0.5)' }}>มีชื่อคล้ายกันในระบบ:</small>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {displaySuggestions.map(s => (
                  <button
                    key={s.id || s.item_name}
                    type="button"
                    onClick={() => handleNameChange(s.item_name)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '15px',
                      border: '1px solid #1abc9c',
                      background: 'rgba(26, 188, 156, 0.1)',
                      color: '#1abc9c',
                      cursor: 'pointer',
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => { e.target.style.background = 'rgba(26, 188, 156, 0.2)'; }}
                    onMouseLeave={(e) => { e.target.style.background = 'rgba(26, 188, 156, 0.1)'; }}
                  >
                    {s.item_name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>ชื่ออื่นๆ / ชื่อย่อ / คำค้นหาเพิ่มเติม (แยกด้วยเครื่องหมายจุลภาค เช่น composite, อุดฟัน)</label>
          <input
            value={regAliases}
            onChange={(e) => setRegAliases(e.target.value)}
            placeholder="เช่น composite, อุดฟัน, บอนดิ้ง..."
            style={{
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#ffffff',
              fontSize: '1em',
              outline: 'none',
              transition: 'all 0.3s'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#1abc9c';
              e.target.style.background = 'rgba(255, 255, 255, 0.12)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              e.target.style.background = 'rgba(255, 255, 255, 0.08)';
            }}
          />
        </div>

        {/* ช่องอัปโหลดรูปภาพ 4 ช่อง */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <label style={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>📸 อัปโหลดรูปภาพวัสดุ</label>

            {/* AI Visual Assist Trigger Button */}
            <button
              type="button"
              onClick={handleAiVisualAssist}
              disabled={isAnalyzingVisual || isAnyUploading || (!fullImg && !zoomImg && !otherImg && !docImg)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: (!fullImg && !zoomImg && !otherImg && !docImg)
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: (!fullImg && !zoomImg && !otherImg && !docImg) ? 'rgba(255, 255, 255, 0.4)' : '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                padding: '6px 14px',
                fontSize: '0.85em',
                fontWeight: 'bold',
                cursor: (!fullImg && !zoomImg && !otherImg && !docImg) || isAnalyzingVisual ? 'not-allowed' : 'pointer',
                boxShadow: (!fullImg && !zoomImg && !otherImg && !docImg) ? 'none' : '0 4px 12px rgba(99, 102, 241, 0.3)',
                transition: 'all 0.2s ease'
              }}
              title={(!fullImg && !zoomImg && !otherImg && !docImg) ? 'กรุณาเลือกรูปภาพก่อนใช้ AI วิเคราะห์' : 'ให้ AI วิเคราะห์ภาพเพื่อระบุชื่อและหมวดหมู่อัตโนมัติ'}
            >
              {isAnalyzingVisual ? (
                <>
                  <div className="sterile-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px', borderTopColor: '#ffffff' }} />
                  <span>กำลังวิเคราะห์ภาพ...</span>
                </>
              ) : (
                <span>✨ AI วิเคราะห์ภาพพัสดุ (Visual ID)</span>
              )}
            </button>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '15px'
          }}>

            {/* ช่อง 1: รูปเต็ม */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.7)', fontWeight: '600' }}>
                1. รูปเต็มของวัสดุ {(regType === 'วัสดุคงทน' || regType === 'หัวกรอ') && '*'}
              </span>
              <label
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 255, 255, 0.04)',
                  border: fullImg ? '2px solid #1abc9c' : '2px dashed rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  padding: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minHeight: '160px',
                  textAlign: 'center',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => { if (!fullImg) { e.currentTarget.style.borderColor = '#1abc9c'; e.currentTarget.style.background = 'rgba(26, 188, 156, 0.05)'; } }}
                onMouseLeave={(e) => { if (!fullImg) { e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)'; } }}
              >
                <input id="full-image-input" type="file" accept="image/*" onChange={(e) => onFileChange(e, 'full')} disabled={isAnyUploading} style={{ display: 'none' }} />

                {isUpFull ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div className="sterile-spinner" style={{ width: '30px', height: '30px', borderWidth: '3px' }} />
                    <span style={{ fontSize: '0.8em', color: '#1abc9c' }}>กำลังอัปโหลด...</span>
                  </div>
                ) : fullImg ? (
                  <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <img
                      src={fullImg}
                      alt="full-preview"
                      onClick={() => setExpandedImage && setExpandedImage(fullImg)}
                      style={{ maxWidth: '100%', maxHeight: '130px', borderRadius: '6px', objectFit: 'contain' }}
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFileRemove('full'); }}
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        background: '#e74c3c',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                      }}
                      title="ลบรูปภาพ"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '2em' }}>📸</span>
                    <span style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.8)' }}>เลือกรูปเต็มวัสดุ</span>
                    <span style={{ fontSize: '0.7em', color: 'rgba(255, 255, 255, 0.4)' }}>คลิกเพื่ออัปโหลด</span>
                  </div>
                )}
              </label>
            </div>

            {/* ช่อง 2: รูปซูม */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.7)', fontWeight: '600' }}>2. รูปซูม (Close-up)</span>
              <label
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 255, 255, 0.04)',
                  border: zoomImg ? '2px solid #3498db' : '2px dashed rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  padding: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minHeight: '160px',
                  textAlign: 'center',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => { if (!zoomImg) { e.currentTarget.style.borderColor = '#3498db'; e.currentTarget.style.background = 'rgba(52, 152, 219, 0.05)'; } }}
                onMouseLeave={(e) => { if (!zoomImg) { e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)'; } }}
              >
                <input id="zoom-image-input" type="file" accept="image/*" onChange={(e) => onFileChange(e, 'zoom')} disabled={isAnyUploading} style={{ display: 'none' }} />

                {isUpZoom ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div className="sterile-spinner" style={{ width: '30px', height: '30px', borderWidth: '3px', borderTopColor: '#3498db' }} />
                    <span style={{ fontSize: '0.8em', color: '#3498db' }}>กำลังอัปโหลด...</span>
                  </div>
                ) : zoomImg ? (
                  <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <img
                      src={zoomImg}
                      alt="zoom-preview"
                      onClick={() => setExpandedImage && setExpandedImage(zoomImg)}
                      style={{ maxWidth: '100%', maxHeight: '130px', borderRadius: '6px', objectFit: 'contain' }}
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFileRemove('zoom'); }}
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        background: '#e74c3c',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                      }}
                      title="ลบรูปภาพ"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '2em' }}>🔍</span>
                    <span style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.8)' }}>เลือกรูปซูมวัสดุ/หัวกรอ</span>
                    <span style={{ fontSize: '0.7em', color: 'rgba(255, 255, 255, 0.4)' }}>คลิกเพื่ออัปโหลด</span>
                  </div>
                )}
              </label>
            </div>

            {/* ช่อง 3: รูปอื่นๆ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.7)', fontWeight: '600' }}>3. รูปอื่นๆ</span>
              <label
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 255, 255, 0.04)',
                  border: otherImg ? '2px solid #9b59b6' : '2px dashed rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  padding: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minHeight: '160px',
                  textAlign: 'center',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => { if (!otherImg) { e.currentTarget.style.borderColor = '#9b59b6'; e.currentTarget.style.background = 'rgba(155, 89, 182, 0.05)'; } }}
                onMouseLeave={(e) => { if (!otherImg) { e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)'; } }}
              >
                <input id="other-image-input" type="file" accept="image/*" onChange={(e) => onFileChange(e, 'other')} disabled={isAnyUploading} style={{ display: 'none' }} />

                {isUpOther ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div className="sterile-spinner" style={{ width: '30px', height: '30px', borderWidth: '3px', borderTopColor: '#9b59b6' }} />
                    <span style={{ fontSize: '0.8em', color: '#9b59b6' }}>กำลังอัปโหลด...</span>
                  </div>
                ) : otherImg ? (
                  <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <img
                      src={otherImg}
                      alt="other-preview"
                      onClick={() => setExpandedImage && setExpandedImage(otherImg)}
                      style={{ maxWidth: '100%', maxHeight: '130px', borderRadius: '6px', objectFit: 'contain' }}
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFileRemove('other'); }}
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        background: '#e74c3c',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                      }}
                      title="ลบรูปภาพ"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '2em' }}>🖼️</span>
                    <span style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.8)' }}>เลือกรูปอื่นๆ</span>
                    <span style={{ fontSize: '0.7em', color: 'rgba(255, 255, 255, 0.4)' }}>คลิกเพื่ออัปโหลด</span>
                  </div>
                )}
              </label>
            </div>

            {/* ช่อง 4: เอกสารใบสั่งซื้อและใบเสร็จ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.7)', fontWeight: '600' }} title="ความละเอียดสูงเป็นพิเศษสำหรับการอ่านข้อความ">
                4. เอกสารใบสั่งซื้อและใบเสร็จ (A4) 💡
              </span>
              <label
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 255, 255, 0.04)',
                  border: docImg ? '2px solid #f1c40f' : '2px dashed rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  padding: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minHeight: '160px',
                  textAlign: 'center',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => { if (!docImg) { e.currentTarget.style.borderColor = '#f1c40f'; e.currentTarget.style.background = 'rgba(241, 196, 15, 0.05)'; } }}
                onMouseLeave={(e) => { if (!docImg) { e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)'; } }}
              >
                <input id="document-image-input" type="file" accept="image/*" onChange={(e) => onFileChange(e, 'document')} disabled={isAnyUploading} style={{ display: 'none' }} />

                {isUpDoc ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div className="sterile-spinner" style={{ width: '30px', height: '30px', borderWidth: '3px', borderTopColor: '#f1c40f' }} />
                    <span style={{ fontSize: '0.8em', color: '#f1c40f' }}>กำลังอัปโหลด...</span>
                  </div>
                ) : docImg ? (
                  <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <img
                      src={docImg}
                      alt="document-preview"
                      onClick={() => setExpandedImage && setExpandedImage(docImg)}
                      style={{ maxWidth: '100%', maxHeight: '130px', borderRadius: '6px', objectFit: 'contain' }}
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFileRemove('document'); }}
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        background: '#e74c3c',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                      }}
                      title="ลบรูปภาพ"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '2em' }}>📄</span>
                    <span style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.8)' }}>เลือกรูปเอกสาร A4</span>
                    <span style={{ fontSize: '0.7em', color: 'rgba(255, 255, 255, 0.4)' }}>คลิกเพื่ออัปโหลด</span>
                  </div>
                )}
              </label>
            </div>

          </div>

          {/* Visual AI Result Banner */}
          {visualAiResult && (
            <div
              style={{
                marginTop: '10px',
                background: 'rgba(99, 102, 241, 0.12)',
                border: '1px solid rgba(139, 92, 246, 0.4)',
                borderRadius: '10px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '10px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.4em' }}>🤖</span>
                <div>
                  <div style={{ fontSize: '0.9em', fontWeight: 'bold', color: '#ffffff' }}>
                    ผลวิเคราะห์: {visualAiResult.thaiName || visualAiResult.className}
                    {visualAiResult.confidence ? ` (ความมั่นใจ ${Math.round(visualAiResult.confidence > 1 ? visualAiResult.confidence : visualAiResult.confidence * 100)}%)` : ''}
                  </div>
                  <div style={{ fontSize: '0.8em', color: 'rgba(255, 255, 255, 0.7)' }}>
                    หมวดหมู่แนะนำ: {visualAiResult.suggestedCategory || 'ทั่วไป'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => applyVisualAiResult()}
                style={{
                  background: '#10b981',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '0.82em',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                ✅ กรอกข้อมูลอัตโนมัติ
              </button>
            </div>
          )}

          {visualAiError && (
            <div
              style={{
                marginTop: '10px',
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '10px 14px',
                color: '#fca5a5',
                fontSize: '0.85em'
              }}
            >
              ⚠️ {visualAiError}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>3. ขนาดบรรจุ</label>
          <input
            value={currentUnitSize}
            onChange={(e) => handleUnitSizeUpdate(e.target.value)}
            placeholder="เช่น 500ml, 10ชิ้น/กล่อง"
            style={{
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#ffffff',
              fontSize: '1em',
              outline: 'none',
              transition: 'all 0.3s'
            }}
            onFocus={(e) => { e.target.style.borderColor = '#1abc9c'; e.target.style.background = 'rgba(255, 255, 255, 0.12)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)'; e.target.style.background = 'rgba(255, 255, 255, 0.08)'; }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>4. หน่วยนับ</label>
          <input
            value={currentUnit}
            onChange={(e) => handleUnitUpdate(e.target.value)}
            placeholder="เช่น ชิ้น, อัน, กล่อง"
            style={{
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#ffffff',
              fontSize: '1em',
              outline: 'none',
              transition: 'all 0.3s'
            }}
            onFocus={(e) => { e.target.style.borderColor = '#1abc9c'; e.target.style.background = 'rgba(255, 255, 255, 0.12)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)'; e.target.style.background = 'rgba(255, 255, 255, 0.08)'; }}
          />
        </div>

        {/* กรอบข้อมูลการจัดซื้อ */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          padding: '20px',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '18px'
        }}>
          <h4 style={{ margin: 0, color: '#3498db', fontSize: '1.1em', fontWeight: '700', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '8px' }}>📊 ข้อมูลการจัดซื้อ / สต็อก</h4>

          {!isEditing && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>จำนวนเริ่มต้น (รับเข้าคลังใหญ่)</label>
              <input
                type="number"
                value={currentInitAmount}
                onChange={(e) => handleInitAmountUpdate(e.target.value)}
                placeholder="0"
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  background: 'rgba(255, 255, 255, 0.06)',
                  color: '#ffffff',
                  fontSize: '0.95em',
                  outline: 'none'
                }}
              />
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>ราคาต่อหน่วย (บาท)</label>
            <input
              type="number"
              value={pricePerUnit}
              onChange={(e) => setPricePerUnit(e.target.value)}
              placeholder="0.00"
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: '#ffffff',
                fontSize: '0.95em',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>Lot No.</label>
            <input
              type="text"
              value={lotNumber}
              onChange={(e) => setLotNumber(e.target.value)}
              placeholder="ระบุเลข Lot..."
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: '#ffffff',
                fontSize: '0.95em',
                outline: 'none'
              }}
            />
          </div>

          {regType === 'วัสดุสิ้นเปลือง' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>วันหมดอายุ</label>
              <input
                type="date"
                value={expDate}
                onChange={(e) => setExpDate(e.target.value)}
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  background: 'rgba(255, 255, 255, 0.06)',
                  color: '#ffffff',
                  fontSize: '0.95em',
                  outline: 'none',
                  colorScheme: 'dark'
                }}
              />
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>ชื่อผู้ผลิต</label>
            <input
              type="text"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder="ชื่อผู้ผลิต..."
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: '#ffffff',
                fontSize: '0.95em',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>บริษัท Supplier</label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="ชื่อบริษัท..."
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: '#ffffff',
                fontSize: '0.95em',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>วันที่รับของ</label>
            <input
              type="date"
              value={receiveDate}
              onChange={(e) => setReceiveDate(e.target.value)}
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: '#ffffff',
                fontSize: '0.95em',
                outline: 'none',
                colorScheme: 'dark'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>เลขที่ใบส่งของ</label>
            <input
              type="text"
              value={deliveryNote}
              onChange={(e) => setDeliveryNote(e.target.value)}
              placeholder="ระบุเลขที่ใบส่ง..."
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: '#ffffff',
                fontSize: '0.95em',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>เลขที่ใบสั่งซื้อ (PO)</label>
            <input
              type="text"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              placeholder="ระบุ PO..."
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: '#ffffff',
                fontSize: '0.95em',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>วันที่สั่งของ</label>
            <input
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: '#ffffff',
                fontSize: '0.95em',
                outline: 'none',
                colorScheme: 'dark'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>วันที่อนุมัติ</label>
            <input
              type="date"
              value={approvalDate}
              onChange={(e) => setApprovalDate(e.target.value)}
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: '#ffffff',
                fontSize: '0.95em',
                outline: 'none',
                colorScheme: 'dark'
              }}
            />
          </div>
        </div>

        {/* ข้อมูลพิกัด วัสดุคงทน และ หัวกรอ */}
        {(regType === 'วัสดุคงทน' || regType === 'หัวกรอ') && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            padding: '20px',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            <h4 style={{ margin: 0, color: '#f39c12', fontSize: '1.1em', fontWeight: '700', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '8px' }}>📍 พิกัดการจัดเก็บ</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>ตู้</label>
              <input
                value={cabinet}
                onChange={(e) => setCabinet(e.target.value)}
                placeholder="ระบุตู้ (เช่น ตู้ 1)"
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  background: 'rgba(255, 255, 255, 0.06)',
                  color: '#ffffff',
                  fontSize: '0.95em',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>ชั้น</label>
              <input
                value={shelf}
                onChange={(e) => setShelf(e.target.value)}
                placeholder="ระบุชั้น (เช่น ชั้น A)"
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  background: 'rgba(255, 255, 255, 0.06)',
                  color: '#ffffff',
                  fontSize: '0.95em',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>แถว</label>
              <input
                value={row}
                onChange={(e) => setRow(e.target.value)}
                placeholder="ระบุแถว (เช่น แถว 2)"
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  background: 'rgba(255, 255, 255, 0.06)',
                  color: '#ffffff',
                  fontSize: '0.95em',
                  outline: 'none'
                }}
              />
            </div>
          </div>
        )}

        {/* ข้อมูลขั้นต่ำ */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          padding: '20px',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px'
        }}>
          <h4 style={{ margin: 0, color: '#e74c3c', fontSize: '1.1em', fontWeight: '700', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '8px' }}>⚠️ การแจ้งเตือนสต็อกขั้นต่ำ</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>ขั้นต่ำรวมทุก Lot</label>
            <input
              type="number"
              value={globalMinStock}
              onChange={(e) => setGlobalMinStock(e.target.value)}
              placeholder="0"
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: '#ffffff',
                fontSize: '0.95em',
                outline: 'none'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.8)' }}>ขั้นต่ำเฉพาะ Lot นี้</label>
            <input
              type="number"
              value={minStock}
              onChange={(e) => setMinStock(e.target.value)}
              placeholder="0"
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: '#ffffff',
                fontSize: '0.95em',
                outline: 'none'
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
          {isEditing && (
            <button
              type="button"
              onClick={resetForm}
              style={{
                flex: 1,
                padding: '14px',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '1.05em',
                fontWeight: 'bold',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.18)'; }}
              onMouseLeave={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.1)'; }}
            >
              ยกเลิก
            </button>
          )}
          <button
            type="submit"
            disabled={isAnyUploading}
            style={{
              flex: 2,
              padding: '14px',
              background: isAnyUploading ? '#555' : 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: isAnyUploading ? 'wait' : 'pointer',
              fontSize: '1.05em',
              fontWeight: 'bold',
              boxShadow: '0 8px 20px rgba(46, 204, 113, 0.25)',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => { if (!isAnyUploading) { e.target.style.boxShadow = '0 12px 30px rgba(46, 204, 113, 0.45)'; e.target.style.transform = 'translateY(-2px)'; } }}
            onMouseLeave={(e) => { if (!isAnyUploading) { e.target.style.boxShadow = '0 8px 20px rgba(46, 204, 113, 0.25)'; e.target.style.transform = 'translateY(0)'; } }}
          >
            {isAnyUploading ? '⏳ กำลังอัปโหลด...' : (isEditing ? 'อัปเดตข้อมูล' : 'บันทึกข้อมูล')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterItemPage;
